import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { AuthStatus } from 'constants/auth';
import {
  CoachStatusEnum,
  useGetUserProfileByUsernameLazyQuery,
  useGetUserPublicProfileByUsernameLazyQuery,
} from 'types/generated/client';
import { type GetProfileSeedDataQuery } from 'types/generated/server';
import { getProfileSeedData } from 'services/server/graphql/queries/getProfileSeedData';
import { useViewer } from 'hooks/useViewer';
import PlayerProfile from 'screens/PlayerProfile';
import ProfilePage from 'screens/ProfilePage';
import MainLayout from 'layouts/MainLayout';
import LoadingSkeleton from 'components/LoadingSkeleton';

const REVALIDATE_IN_SECONDS = 24 * 60 * 60 * 1;
type PageProps = GetProfileSeedDataQuery['users'][0];

export const getStaticProps: GetStaticProps<PageProps, { username: string }> = async ({
  params,
}) => {
  const username = params?.username;

  if (!username) {
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  const data = await getProfileSeedData({ username: username.toLowerCase() });
  const user = data?.users?.[0];

  if (!user) {
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  return {
    props: {
      ...user,
    },
    revalidate: REVALIDATE_IN_SECONDS,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: true,
  };
};

/**
 * The original coach-oriented profile. Kept as-is behind the coach branch; it owns
 * its own page chrome, which is why the player branch is wrapped separately below.
 */
function CoachProfileRoute(props: PageProps) {
  const router = useRouter();
  const viewer = useViewer();
  const [getUserProfileByUsernameQuery, { data, loading, called }] =
    useGetUserProfileByUsernameLazyQuery();
  const [
    getUserPublicProfileByUsernameQuery,
    { data: publicData, loading: publicLoading, called: publicCalled },
  ] = useGetUserPublicProfileByUsernameLazyQuery();
  const profile = data?.userProfiles[0] || publicData?.userProfiles[0];
  const followingDetails = data?.userFollows?.[0];
  const username = typeof router?.query?.username === 'string' ? router.query.username : '';
  const isPublicLoading =
    viewer.status === AuthStatus.Anonymous && (publicLoading || !publicCalled);
  const isAuthedLoading = viewer.status === AuthStatus.User && (loading || !called);
  const isLoading = viewer.status === AuthStatus.Loading || isPublicLoading || isAuthedLoading;

  React.useEffect(() => {
    if (router.isReady && router.query.username && typeof router.query.username === 'string') {
      if (viewer.status !== AuthStatus.Loading) {
        if (viewer.status === AuthStatus.User && viewer.userId) {
          getUserProfileByUsernameQuery({
            variables: {
              username: router.query.username.toLowerCase(),
              viewerId: viewer.userId,
            },
          }).catch((error) => Sentry.captureException(error));
        } else {
          getUserPublicProfileByUsernameQuery({
            variables: {
              username: router.query.username.toLowerCase(),
            },
          }).catch((error) => Sentry.captureException(error));
        }
      }
    }
  }, [viewer.userId, viewer.status, router.isReady]);

  if (!loading && called && (!data?.userProfiles || data?.userProfiles.length === 0)) {
    // TODO: 404 this page
  }

  return (
    <ProfilePage
      profile={profile}
      followingDetails={followingDetails}
      isViewerProfile={!!viewer.userId && !!profile?.id && viewer.userId === profile.id}
      isLoading={isLoading}
      username={props.username || username}
      fullName={props.fullName}
    />
  );
}

export default function UserProfilePage(props: PageProps) {
  const router = useRouter();
  // Declared before the `isFallback` return so the hook order stays stable.
  const viewer = useViewer();
  const username = typeof router?.query?.username === 'string' ? router.query.username : '';

  // `fallback: true` renders once with no props, so the coach/player branch isn't
  // decided yet. Holding here avoids mounting the wrong profile and swapping it out.
  if (router.isFallback) {
    return (
      <MainLayout mainClassName="pb-12">
        <div className="container mx-auto flex max-w-[64rem] flex-col gap-ds-lg pt-8">
          <LoadingSkeleton height="8.75rem" />
          <LoadingSkeleton height="2.5rem" count={3} />
        </div>
      </MainLayout>
    );
  }

  if (props.coachStatus === CoachStatusEnum.Active) {
    return <CoachProfileRoute {...props} />;
  }

  return (
    <MainLayout
      mainClassName="pb-12"
      // safeAreaProps replaces the default object, so isHideSidebar must be repeated.
      safeAreaProps={{ isShowTopNav: viewer.status !== AuthStatus.User, isHideSidebar: true }}
    >
      <PlayerProfile username={props.username || username} seedName={props.fullName} />
    </MainLayout>
  );
}
